var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import * as THREE from 'three';
import { OrbFactory } from '../../orbs/OrbFactory';
import { scheduleOrbSpawn } from '../physics/OrbSpawnScheduler';
import { getStackRegionOrb } from '../creation/DynamicSystemDesignModel';
import { global } from '../../core/GlobalState';
import { OrbConnectionManager } from '../connections/OrbConnectionManager';
import { computeRingLayout, RingOrbitAnimator } from '../../utils/RingLayout';
export function loadInitialOrbs() {
    var _a;
    if (typeof window === 'undefined')
        return [];
    return ((_a = window.__ORBS__) !== null && _a !== void 0 ? _a : []);
}
function isObject(value) {
    return typeof value === 'object' && value !== null;
}
function isBaseOrbRecord(value) {
    return isObject(value) && typeof value.orbId === 'string' && value.orbId.length > 0;
}
function isDirection(value) {
    return value === 0 || value === 1 || value === 2;
}
function normalizeRelationship(value) {
    if (!isObject(value))
        return null;
    if (typeof value.orbId !== 'string' || value.orbId.length === 0)
        return null;
    if (!isDirection(value.direction))
        return null;
    return value;
}
function isInitialOrbSetRecord(value) {
    return (isObject(value)
        && ((typeof value.orbId === 'string' && value.orbId.length > 0)
            || (typeof value.orbSetId === 'string' && value.orbSetId.length > 0))
        && typeof value.orbType === 'string'
        && value.orbType.length > 0
        && Array.isArray(value.records));
}
function normalizeIncomingOrbs(raw) {
    const toRecords = () => {
        // Preferred shape for orbs.json: array of orb sets.
        // We validate only required base fields and tolerate optional/missing/new fields.
        if (Array.isArray(raw) && raw.every((entry) => isInitialOrbSetRecord(entry))) {
            return raw.flatMap((set) => set.records
                .filter(isBaseOrbRecord)
                .map((record) => { var _a; return (Object.assign(Object.assign({}, record), { orbType: (_a = record.orbType) !== null && _a !== void 0 ? _a : set.orbType })); }));
        }
        if (Array.isArray(raw) && raw.every((entry) => isBaseOrbRecord(entry)))
            return raw;
        if (!Array.isArray(raw)) {
            if (Array.isArray(raw.records))
                return raw.records;
            if (Array.isArray(raw.orbSets)) {
                return raw.orbSets.flatMap((set) => (Array.isArray(set.records)
                    ? set.records.map((record) => { var _a; return (Object.assign(Object.assign({}, record), { orbType: (_a = record.orbType) !== null && _a !== void 0 ? _a : set.orbType })); })
                    : []));
            }
        }
        return [];
    };
    return toRecords().map((record, index) => ({
        orbId: typeof record.orbId === 'string' && record.orbId.length > 0 ? record.orbId : `ssi-orb-${index + 1}`,
        orbType: record.orbType,
        label: record.label,
        data: record.data,
        icon: typeof record.icon === 'string' ? record.icon : undefined,
        payload: isObject(record.payload) ? record.payload : undefined,
        orbRelationships: Array.isArray(record.orbRelationships)
            ? record.orbRelationships
                .map((relation) => normalizeRelationship(relation))
                .filter((relation) => relation !== null)
            : undefined,
    }));
}
function resolveDockerRegionAnchor() {
    var _a;
    const systemScene = global.sceneManager.getScene('system');
    const dockerRegion = getStackRegionOrb(systemScene, 'Docker Container Region');
    if ((_a = dockerRegion === null || dockerRegion === void 0 ? void 0 : dockerRegion.group) === null || _a === void 0 ? void 0 : _a.position) {
        return dockerRegion.group.position.clone();
    }
    return new THREE.Vector3(-6500, 1200, -800);
}
const DOCKER_STACK_ORDER = ['core', 'data', 'platform', 'observability', 'homelab', 'ai', 'piserver', 'generated', 'externalsites'];
// TODO: replace hardcoded stack order with api.orbscape.io/deploymentorder or SSR-provided ordering.
const orbitRuntimeByScene = new Map();
const lineRuntimeByScene = new Map();
const dockerRingAnimatorsByScene = new Map();
function calculateDockerLayerCylinderHeight(ringItemCount) {
    const baseHeight = 160;
    const growthPerItem = 15;
    return baseHeight + Math.max(0, ringItemCount - 5) * growthPerItem;
}
function calculateDockerLayerSizing(activeStacks, byLayer) {
    return activeStacks.map((stackName, stackPosition) => {
        var _a;
        const layer = (_a = byLayer.get(stackName)) !== null && _a !== void 0 ? _a : [];
        const stackItemCount = layer.length;
        const ringItemCount = Math.max(0, stackItemCount - 1);
        const baseRingRadius = 980 + ringItemCount * 220;
        const reducedSpreadRadius = Math.max(1700, baseRingRadius * (2 / 3));
        return {
            stackName,
            stackPosition,
            ringRadius: reducedSpreadRadius,
            cylinderHeight: calculateDockerLayerCylinderHeight(ringItemCount),
        };
    });
}
function resizeDockerRegionEnvelope(sceneName, envelope) {
    const scene = global.sceneManager.getScene(sceneName);
    const dockerRegion = getStackRegionOrb(scene, 'Docker Container Region');
    const group = dockerRegion === null || dockerRegion === void 0 ? void 0 : dockerRegion.group;
    if (!group)
        return;
    const nominalRadius = 4800;
    const desiredRadius = Math.max(2600, envelope.maxRingRadius + 920);
    const radialScale = desiredRadius / nominalRadius;
    const nominalHeight = 900;
    const desiredHeight = Math.max(420, (envelope.maxY - envelope.minY) * 0.45 + 110);
    const verticalScale = desiredHeight / nominalHeight;
    group.scale.set(radialScale, verticalScale, radialScale);
}
function extractDockerMeta(record) {
    var _a, _b, _c, _d;
    const raw = String((_c = (_a = record.label) !== null && _a !== void 0 ? _a : (_b = record.data) === null || _b === void 0 ? void 0 : _b.Name) !== null && _c !== void 0 ? _c : '').trim();
    const [prefix = '', instancePart = '0'] = raw.split('.');
    const prefixParts = prefix.split('_').filter(Boolean);
    const stackName = ((_d = prefixParts[0]) !== null && _d !== void 0 ? _d : 'generated').toLowerCase();
    const serviceName = (prefixParts.slice(1).join('_') || prefix || 'container').toLowerCase();
    const instanceNumber = instancePart;
    return { stackName, serviceName, instanceNumber };
}
function animateOrbToPosition(orb, target, durationMs = 550) {
    const group = orb.group;
    if (!group)
        return;
    const start = group.position.clone();
    const startAt = performance.now();
    const step = (now) => {
        const t = Math.min(1, (now - startAt) / durationMs);
        const eased = 1 - Math.pow(1 - t, 3);
        group.position.lerpVectors(start, target, eased);
        if (t < 1) {
            requestAnimationFrame(step);
        }
    };
    requestAnimationFrame(step);
}
function placeDockerOrbset(sceneName, records, anchor) {
    var _a, _b, _c;
    const placedByOrbId = (_a = orbitRuntimeByScene.get(sceneName)) !== null && _a !== void 0 ? _a : new Map();
    orbitRuntimeByScene.set(sceneName, placedByOrbId);
    const connectionManager = OrbConnectionManager.getInstance();
    const existingLines = (_b = lineRuntimeByScene.get(sceneName)) !== null && _b !== void 0 ? _b : new Set();
    const nextLines = new Set();
    const normalized = records.map((record) => {
        var _a, _b, _c, _d;
        const meta = extractDockerMeta(record);
        const mergedData = Object.assign(Object.assign({}, ((_a = record.data) !== null && _a !== void 0 ? _a : {})), { stackName: meta.stackName, serviceName: meta.serviceName, instanceNumber: meta.instanceNumber, orbId: record.orbId, IconURL: (_c = (_b = record.data) === null || _b === void 0 ? void 0 : _b.IconURL) !== null && _c !== void 0 ? _c : record.icon });
        record.data = mergedData;
        record.payload = Object.assign(Object.assign({}, ((_d = record.payload) !== null && _d !== void 0 ? _d : {})), { bootstrapMeta: meta });
        record.label = `${meta.serviceName}.${meta.instanceNumber}`;
        return { record, meta };
    });
    const stackPriority = new Map(DOCKER_STACK_ORDER.map((name, index) => [name, index]));
    normalized.sort((a, b) => { var _a, _b; return ((_a = stackPriority.get(a.meta.stackName)) !== null && _a !== void 0 ? _a : 100) - ((_b = stackPriority.get(b.meta.stackName)) !== null && _b !== void 0 ? _b : 100); });
    const byLayer = new Map();
    normalized.forEach((entry) => {
        var _a;
        const bucket = (_a = byLayer.get(entry.meta.stackName)) !== null && _a !== void 0 ? _a : [];
        bucket.push(entry);
        byLayer.set(entry.meta.stackName, bucket);
    });
    const spawnTasks = [];
    const liveIds = new Set(records.map((record) => record.orbId));
    let minY = Number.POSITIVE_INFINITY;
    let maxY = Number.NEGATIVE_INFINITY;
    let maxRingRadius = 0;
    const activeStacks = DOCKER_STACK_ORDER.filter((name) => { var _a, _b; return ((_b = (_a = byLayer.get(name)) === null || _a === void 0 ? void 0 : _a.length) !== null && _b !== void 0 ? _b : 0) > 0; });
    const stackSizing = calculateDockerLayerSizing(activeStacks, byLayer);
    const interLayerGap = 120;
    const totalHeight = stackSizing.reduce((sum, entry) => sum + entry.cylinderHeight, 0)
        + Math.max(0, stackSizing.length - 1) * interLayerGap;
    const stackOriginY = anchor.y - totalHeight / 2;
    let cursorY = stackOriginY;
    stackSizing.forEach(({ stackName, stackPosition, ringRadius, cylinderHeight }, layerIndex) => {
        var _a, _b, _c, _d, _e;
        const layer = (_a = byLayer.get(stackName)) !== null && _a !== void 0 ? _a : [];
        if (layer.length === 0)
            return;
        const y = cursorY + cylinderHeight / 2;
        cursorY += cylinderHeight;
        if (layerIndex < stackSizing.length - 1) {
            cursorY += interLayerGap;
        }
        const centerCandidate = stackName === 'piserver'
            ? layer.find((entry) => entry.meta.serviceName === 'objectexplorer')
            : stackName === 'platform'
                ? layer.find((entry) => entry.meta.serviceName === 'eventorchestrator')
                : undefined;
        const ringItems = centerCandidate ? layer.filter((item) => item.record.orbId !== centerCandidate.record.orbId) : layer;
        maxRingRadius = Math.max(maxRingRadius, ringRadius);
        const ringCenter = new THREE.Vector3(anchor.x, y, anchor.z);
        minY = Math.min(minY, y - cylinderHeight / 2);
        maxY = Math.max(maxY, y + cylinderHeight / 2);
        const ringLayoutItems = ringItems.map((entry) => ({
            id: entry.record.orbId,
            radius: 150,
            angularVelocity: 0.38 + (stackPosition * 0.04),
        }));
        const layerClockwise = stackPosition % 2 === 0;
        const ringLayout = computeRingLayout(ringLayoutItems, {
            centre: ringCenter,
            normal: new THREE.Vector3(0, 1, 0),
            radius: ringRadius,
            angularVelocity: 0.38 + (stackPosition * 0.04),
            clockwise: layerClockwise,
            startAngle: stackPosition * 0.42,
        });
        const sceneAnimators = (_b = dockerRingAnimatorsByScene.get(sceneName)) !== null && _b !== void 0 ? _b : new Map();
        dockerRingAnimatorsByScene.set(sceneName, sceneAnimators);
        const stackAnimator = (_c = sceneAnimators.get(stackName)) !== null && _c !== void 0 ? _c : new RingOrbitAnimator(true);
        sceneAnimators.set(stackName, stackAnimator);
        if (centerCandidate) {
            const centerPos = ringCenter.clone();
            const existing = placedByOrbId.get(centerCandidate.record.orbId);
            if (existing) {
                animateOrbToPosition(existing, centerPos);
            }
            else {
                const created = OrbFactory.createOrbByType((_d = centerCandidate.record.orbType) !== null && _d !== void 0 ? _d : 'DOCKER_CONTAINER_ORB', centerCandidate.record.label, (_e = centerCandidate.record.data) !== null && _e !== void 0 ? _e : {});
                if (created) {
                    spawnTasks.push(scheduleOrbSpawn({ orb: created, origin: centerPos, direction: new THREE.Vector3(0, 0, 0), sprayCoefficient: 0, baseVelocity: 0.001, sceneName, availableAt: performance.now() }));
                    placedByOrbId.set(centerCandidate.record.orbId, created);
                }
            }
        }
        ringItems.forEach((entry, index) => {
            var _a, _b, _c, _d;
            const placement = ringLayout.placements[index];
            const target = (_b = (_a = placement === null || placement === void 0 ? void 0 : placement.position) === null || _a === void 0 ? void 0 : _a.clone()) !== null && _b !== void 0 ? _b : ringCenter.clone();
            const existing = placedByOrbId.get(entry.record.orbId);
            if (existing) {
                if (existing.group && placement) {
                    stackAnimator.update({
                        object: existing.group,
                        placement,
                        centreProvider: () => ringCenter.clone(),
                    });
                }
                return;
            }
            const orb = OrbFactory.createOrbByType((_c = entry.record.orbType) !== null && _c !== void 0 ? _c : 'DOCKER_CONTAINER_ORB', entry.record.label, (_d = entry.record.data) !== null && _d !== void 0 ? _d : {});
            if (!orb)
                return;
            spawnTasks.push(scheduleOrbSpawn({ orb, origin: target, direction: new THREE.Vector3(0, 0, 0), sprayCoefficient: 0, baseVelocity: 0.001, sceneName, availableAt: performance.now() + index * global.baseTimeGap }));
            placedByOrbId.set(entry.record.orbId, orb);
            if (orb.group && placement) {
                stackAnimator.add({
                    object: orb.group,
                    placement,
                    centreProvider: () => ringCenter.clone(),
                });
            }
        });
    });
    const serviceToOrb = new Map();
    normalized.forEach(({ meta, record }) => {
        const orb = placedByOrbId.get(record.orbId);
        if (orb)
            serviceToOrb.set(meta.serviceName, orb);
    });
    const overrides = [
        { source: 'nginx', target: 'gatewayservice', deltaY: 350, lineKey: 'gatewayservice->nginx' },
        { source: 'registry', target: 'artifactservice', deltaY: -350, lineKey: 'artifactservice->registry' },
    ];
    overrides.forEach(({ source, target, deltaY, lineKey }) => {
        const sourceOrb = serviceToOrb.get(source);
        const targetOrb = serviceToOrb.get(target);
        if (!(sourceOrb === null || sourceOrb === void 0 ? void 0 : sourceOrb.group) || !(targetOrb === null || targetOrb === void 0 ? void 0 : targetOrb.group))
            return;
        const targetPos = targetOrb.group.position.clone().add(new THREE.Vector3(0, deltaY, 0));
        animateOrbToPosition(sourceOrb, targetPos);
        nextLines.add(lineKey);
        if (!existingLines.has(lineKey)) {
            connectionManager.connect({ a: targetOrb, b: sourceOrb, labelText: lineKey });
        }
    });
    for (const [orbId, orb] of placedByOrbId.entries()) {
        if (liveIds.has(orbId))
            continue;
        connectionManager.disconnectAllFor(orb);
        if (orb.group) {
            const sceneAnimators = dockerRingAnimatorsByScene.get(sceneName);
            sceneAnimators === null || sceneAnimators === void 0 ? void 0 : sceneAnimators.forEach((animator) => animator.remove(orb.group));
        }
        if ((_c = orb.group) === null || _c === void 0 ? void 0 : _c.uuid) {
            global.orbManager.hardRemoveOrb(orb.group.uuid);
        }
        placedByOrbId.delete(orbId);
    }
    if (Number.isFinite(minY) && Number.isFinite(maxY) && maxRingRadius > 0) {
        resizeDockerRegionEnvelope(sceneName, { maxRingRadius, minY, maxY });
    }
    lineRuntimeByScene.set(sceneName, nextLines);
    return spawnTasks;
}
function resolveOrbTypeAnchor(sceneName, orbType, dockerAnchor) {
    var _a, _b, _c;
    const scene = global.sceneManager.getScene(sceneName);
    const regionByType = {
        GITHUB_ORB: 'GitHub Repository Region',
        DOMAIN_ORB: 'Domain Region',
        COMPUTER_ORB: 'Computer Region',
        DATABASE_OBJECT_ORB: 'Database Object Region',
        CONTAINER_IMAGE_ORB: 'Container Image Region',
        NETWORK_GATEWAY_ORB: 'Network Gateway Region',
        ENDPOINT_ORB: 'Container Endpoint Region',
    };
    const regionLabel = regionByType[orbType];
    if (!regionLabel)
        return dockerAnchor.clone();
    const regionOrb = getStackRegionOrb(scene, regionLabel);
    return (_c = (_b = (_a = regionOrb === null || regionOrb === void 0 ? void 0 : regionOrb.group) === null || _a === void 0 ? void 0 : _a.position) === null || _b === void 0 ? void 0 : _b.clone()) !== null && _c !== void 0 ? _c : dockerAnchor.clone();
}
function placeFlatOrbset(sceneName, records, orbType, anchor) {
    var _a, _b;
    const runtimeKey = `${sceneName}:${orbType}`;
    const placedByOrbId = (_a = orbitRuntimeByScene.get(runtimeKey)) !== null && _a !== void 0 ? _a : new Map();
    orbitRuntimeByScene.set(runtimeKey, placedByOrbId);
    const spawnTasks = [];
    const liveIds = new Set(records.map((record) => record.orbId));
    const radius = Math.max(900, 420 + records.length * 70);
    records.forEach((record, index) => {
        var _a, _b, _c;
        const angle = (index / Math.max(records.length, 1)) * Math.PI * 2;
        const yOffset = orbType === 'DOMAIN_ORB' ? 80 : -80;
        const target = new THREE.Vector3(anchor.x + Math.cos(angle) * radius, anchor.y + yOffset, anchor.z + Math.sin(angle) * radius);
        const existing = placedByOrbId.get(record.orbId);
        if (existing) {
            animateOrbToPosition(existing, target);
            return;
        }
        const orbData = Object.assign(Object.assign(Object.assign({}, ((_a = record.data) !== null && _a !== void 0 ? _a : {})), ((_b = record.payload) !== null && _b !== void 0 ? _b : {})), { orbId: record.orbId });
        const orb = OrbFactory.createOrbByType((_c = record.orbType) !== null && _c !== void 0 ? _c : orbType, record.label, orbData);
        if (!orb)
            return;
        spawnTasks.push(scheduleOrbSpawn({ orb, origin: target, direction: new THREE.Vector3(0, 0, 0), sprayCoefficient: 0, baseVelocity: 0.001, sceneName, availableAt: performance.now() + index * global.baseTimeGap }));
        placedByOrbId.set(record.orbId, orb);
    });
    for (const [orbId, orb] of placedByOrbId.entries()) {
        if (liveIds.has(orbId))
            continue;
        OrbConnectionManager.getInstance().disconnectAllFor(orb);
        if ((_b = orb.group) === null || _b === void 0 ? void 0 : _b.uuid)
            global.orbManager.hardRemoveOrb(orb.group.uuid);
        placedByOrbId.delete(orbId);
    }
    return spawnTasks;
}
export function bootstrapInitialOrbsFromSsi() {
    return __awaiter(this, void 0, void 0, function* () {
        const records = normalizeIncomingOrbs(loadInitialOrbs());
        if (records.length === 0)
            return;
        const anchor = resolveDockerRegionAnchor();
        const sceneName = 'system';
        const dockerRecords = records.filter((record) => { var _a; return ((_a = record.orbType) !== null && _a !== void 0 ? _a : 'DOCKER_CONTAINER_ORB') === 'DOCKER_CONTAINER_ORB'; });
        const githubRecords = records.filter((record) => record.orbType === 'GITHUB_ORB');
        const domainRecords = records.filter((record) => record.orbType === 'DOMAIN_ORB');
        const computerRecords = records.filter((record) => record.orbType === 'COMPUTER_ORB');
        const databaseRecords = records.filter((record) => record.orbType === 'DATABASE_OBJECT_ORB');
        const imageRecords = records.filter((record) => record.orbType === 'CONTAINER_IMAGE_ORB');
        const networkGatewayRecords = records.filter((record) => record.orbType === 'NETWORK_GATEWAY_ORB');
        const endpointRecords = records.filter((record) => record.orbType === 'ENDPOINT_ORB' || record.orbType === 'CONTAINER_ENDPOINT_ORB');
        const normalizedEndpointRecords = endpointRecords.map((record) => (Object.assign(Object.assign({}, record), { orbType: 'CONTAINER_ENDPOINT_ORB' })));
        const logBootstrapSet = (orbType, count) => {
            if (count === 0) {
                console.info(`[OrbBootstrap] No records found for ${orbType}.`);
                return;
            }
            console.info(`[OrbBootstrap] Bootstrapping ${count} records for ${orbType}.`);
        };
        logBootstrapSet('DOCKER_CONTAINER_ORB', dockerRecords.length);
        logBootstrapSet('GITHUB_ORB', githubRecords.length);
        logBootstrapSet('DOMAIN_ORB', domainRecords.length);
        logBootstrapSet('COMPUTER_ORB', computerRecords.length);
        logBootstrapSet('DATABASE_OBJECT_ORB', databaseRecords.length);
        logBootstrapSet('CONTAINER_IMAGE_ORB', imageRecords.length);
        logBootstrapSet('NETWORK_GATEWAY_ORB', networkGatewayRecords.length);
        logBootstrapSet('ENDPOINT_ORB', endpointRecords.length);
        const spawnTasks = [];
        spawnTasks.push(...placeDockerOrbset(sceneName, dockerRecords, anchor));
        spawnTasks.push(...placeFlatOrbset(sceneName, githubRecords, 'GITHUB_ORB', resolveOrbTypeAnchor(sceneName, 'GITHUB_ORB', anchor)));
        spawnTasks.push(...placeFlatOrbset(sceneName, domainRecords, 'DOMAIN_ORB', resolveOrbTypeAnchor(sceneName, 'DOMAIN_ORB', anchor)));
        spawnTasks.push(...placeFlatOrbset(sceneName, computerRecords, 'COMPUTER_ORB', resolveOrbTypeAnchor(sceneName, 'COMPUTER_ORB', anchor)));
        spawnTasks.push(...placeFlatOrbset(sceneName, databaseRecords, 'DATABASE_OBJECT_ORB', resolveOrbTypeAnchor(sceneName, 'DATABASE_OBJECT_ORB', anchor)));
        spawnTasks.push(...placeFlatOrbset(sceneName, imageRecords, 'CONTAINER_IMAGE_ORB', resolveOrbTypeAnchor(sceneName, 'CONTAINER_IMAGE_ORB', anchor)));
        spawnTasks.push(...placeFlatOrbset(sceneName, networkGatewayRecords, 'NETWORK_GATEWAY_ORB', resolveOrbTypeAnchor(sceneName, 'NETWORK_GATEWAY_ORB', anchor)));
        spawnTasks.push(...placeFlatOrbset(sceneName, normalizedEndpointRecords, 'CONTAINER_ENDPOINT_ORB', resolveOrbTypeAnchor(sceneName, 'ENDPOINT_ORB', anchor)));
        yield Promise.allSettled(spawnTasks);
    });
}
